/* eslint-disable consistent-return */
import 'whatwg-fetch';
import { backOff } from 'exponential-backoff';
import { LocalStorageKeys } from 'containers/Security/modules/constants';

type Params = {
  headers: Headers;
  method: string;
};
function processResponse(
  response: Response,
  request: {
    mergeParam: Params;
    url: string;
    contentType: string | null | undefined;
  },
) {
  if (response.status >= 200 && response.status < 300) {
    if (request.contentType && contentTypes.includes(request.contentType)) {
      return response;
    }
    if (response.status === 204) {
      return response.text();
    }
    try {
      return response.json();
    } catch (error) {
      console.error(error);
    }
  }
}

/**
 * Authorization header
 */
function mergeOptions(
  options: any,
  includeIdentity: boolean,
  contentType: string | null | undefined,
  includeTenantId: boolean,
) {
  let standardOptions;

  // Get the latest user from local storage
  const user_access_token = localStorage.getItem(LocalStorageKeys.user_access_token);

  const requestHeaders = new Headers({
    Accept: 'application/json',
  });

  requestHeaders.append('Authorization', `Bearer ${user_access_token}`);

  if (contentType === 'attachment') {
    standardOptions = {
      headers: requestHeaders,
    };
  } else if (contentType === 'pdf') {
    requestHeaders.append('Accept', 'application/pdf');
    standardOptions = {
      headers: requestHeaders,
    };
  } else {
    requestHeaders.append('Content-Type', 'application/json');
    standardOptions = {
      headers: requestHeaders,
    };
  }
  // Object.assign() doesn't handle deep merge
  const finalOptions = { ...standardOptions, ...options };
  return finalOptions;
}

function stringify(params) {
  return Object.keys(params)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
const contentTypes = ['pdf', 'file', 'provider', 'pdfPost', 'xlsx'];

export default async function request(
  url: string,
  options: { method?: string; body?: string } = {},
  contentType?: string | null,
  params = null,
  includeIdentity = true,
  includeTenantId = false,
) {
  const tryRequest = () => {
    let uri = url;
    if (uri.includes('/pmlistapi/')) {
      includeTenantId = true;
    }
    if (params) {
      uri = `${uri}?${stringify(params)}`;
    }
    let mergeParams = mergeOptions(options, includeIdentity, contentType, includeTenantId);
    if (contentType === 'pdf' && options.method === 'post') {
      mergeParams = mergeOptions(
        options,
        includeIdentity,
        (contentType = 'pdfPost'),
        includeTenantId,
      );
    }

    // If the contentType is attachment, do an xhr request instead of fetch
    if (contentType === 'attachment') {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(mergeParams.method, uri);
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // Get the Response from the xhr request
            const response: Response = xhr.response;
            processResponse(response, { mergeParam: mergeParams, url, contentType });
            resolve(xhr.response);
          } else {
            reject(xhr.statusText);
          }
        };
        xhr.onerror = () => reject(xhr.statusText);
        // Set the Authorization header and multipart/form-data header
        xhr.setRequestHeader(
          'Authorization',
          `Bearer ${localStorage.getItem(LocalStorageKeys.user_access_token)}`,
        );
        xhr.send(mergeParams.body);
      });
    }

    return fetch(uri, mergeParams)
      .then((response) =>
        //@ts-ignore
        processResponse(response, { mergeParams, url, contentType }),
      )
      .catch((res) => {
        throw res;
      });
  };
  try {
    const response = await backOff(() => tryRequest(), {
      numOfAttempts: 1,
      retry: (e, attemptNumber) => {
        // tslint:disable-next-line: no-console
        console.error('Request Attempt #', attemptNumber);
        // tslint:disable-next-line: no-console
        console.error('Error in retry request', e);
        return true;
      },
    });
    return response;
  } catch (e) {
    // tslint:disable-next-line: no-console
    console.error('Error in request, no more retries left', e);
    throw e;
  }
}
